import type { ReactNode } from "react";
import { createContext, useEffect, useMemo, useState } from "react";
import { loadMapLabelFont } from "../assets/mapLabelFont";
import type { CustomViewport } from "../CustomViewport";
import type { MapCoordinates } from "../MapCoordinates";
import {
    InteractionManager,
    NativeCullingManager,
    ViewportEffectsManager,
} from "../managers";

export interface SceneContextValue {
    viewport: CustomViewport;
    interactionManager: InteractionManager;
    nativeCullingManager: NativeCullingManager;
    viewportEffectsManager: ViewportEffectsManager;
    coordinates: MapCoordinates;
    labelFontReady: boolean;
}

export const SceneContext = createContext<SceneContextValue | null>(null);

interface SceneProviderProps {
    viewport: CustomViewport;
    coordinates: MapCoordinates;
    children: ReactNode;
}

export function SceneProvider({
    viewport,
    coordinates,
    children,
}: SceneProviderProps) {
    const [interactionManager] = useState(() => new InteractionManager());
    const [nativeCullingManager] = useState(() => new NativeCullingManager());
    const [viewportEffectsManager] = useState(
        () => new ViewportEffectsManager(),
    );
    const [labelFontReady, setLabelFontReady] = useState(false);

    useEffect(() => {
        return () => interactionManager.destroy();
    }, [interactionManager]);
    useEffect(() => {
        interactionManager.connect(viewport);
        return () => interactionManager.disconnect(viewport);
    }, [interactionManager, viewport]);
    useEffect(() => {
        nativeCullingManager.connect(viewport);
        return () => nativeCullingManager.disconnect(viewport);
    }, [nativeCullingManager, viewport]);
    useEffect(() => {
        viewportEffectsManager.connect(viewport);
        return () => viewportEffectsManager.disconnect(viewport);
    }, [viewportEffectsManager, viewport]);

    useEffect(() => {
        let mounted = true;

        void loadMapLabelFont()
            .then(() => {
                if (mounted) setLabelFontReady(true);
            })
            .catch((error: unknown) => {
                console.error("Font load failed", error);
            });

        return () => {
            mounted = false;
        };
    }, []);

    const value = useMemo(
        () => ({
            viewport,
            interactionManager,
            nativeCullingManager,
            viewportEffectsManager,
            coordinates,
            labelFontReady,
        }),
        [
            viewport,
            interactionManager,
            nativeCullingManager,
            viewportEffectsManager,
            coordinates,
            labelFontReady,
        ],
    );

    return (
        <SceneContext.Provider value={value}>{children}</SceneContext.Provider>
    );
}
